
import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { blogPosts } from '@/data/blog-posts';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import { Calendar } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import SEO from '@/components/SEO';

// Helper function to transform YouTube URLs into embed codes
const processYoutubeUrl = (url: string) => {
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/;
  const match = url.match(youtubeRegex);
  
  if (match && match[1]) {
    return `<div class="video-container">
      <iframe 
        src="https://www.youtube.com/embed/${match[1]}" 
        title="YouTube video player" 
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowfullscreen
      ></iframe>
    </div>`;
  }
  
  return url;
};

// Process the content to handle YouTube videos
const processContent = (content: string | undefined) => {
  // Guard clause for undefined or non-string content
  if (!content) return '';
  
  // Check if content is already HTML
  if (content.trim().startsWith('<')) {
    return content;
  }
  
  // Process YouTube URLs
  const lines = content.split('\n');
  const processedLines = lines.map(line => {
    if (line.trim().startsWith('https://www.youtube.com/') || line.trim().startsWith('https://youtu.be/')) {
      return processYoutubeUrl(line);
    }
    return line;
  });
  
  return processedLines.join('\n');
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((post) => post.slug === slug);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // If post not found, redirect to blog list
  if (!post) {
    return <Navigate to="/blog" replace />;
  }
  
  // Process content to handle YouTube videos
  const processedContent = processContent(post.content);
  
  return (
    <div className="flex flex-col min-h-screen bg-dark-bg">
      <SEO
        title={`SkyTest.ai Blog - ${post.title}`}
        description={post.summary}
        ogImage={post.thumbnail.startsWith('http') ? post.thumbnail : post.thumbnail}
      />
      <Navbar />
      
      <main className="flex-grow pt-24 pb-20">
        {/* Hero Banner */}
        <div className="w-full h-[40vh] relative mb-10">
          <div className="absolute inset-0">
            <img 
              src={post.thumbnail} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-bg to-transparent" />
          </div>
          
          <div className="container mx-auto h-full relative z-10 flex items-end pb-8">
            <div className="max-w-4xl">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {post.title}
              </h1>
              
              <div className="flex items-center text-sm text-gray-300">
                <Calendar size={16} className="mr-1.5" />
                <time dateTime={post.date}>{formatDate(post.date)}</time>
              </div>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="container mx-auto px-1.5 md:px-6 lg:px-12">
          <div className="max-w-3xl mx-auto">
            {processedContent ? (
              <div 
                className="blog-content prose prose-invert prose-lg prose-headings:text-white prose-headings:font-bold prose-p:text-gray-300 prose-a:text-brand hover:prose-a:text-blue-50 prose-blockquote:border-brand prose-blockquote:bg-dark-card prose-blockquote:p-6 prose-blockquote:rounded-lg prose-li:text-gray-300 prose-ul:list-disc prose-ol:list-decimal prose-img:rounded-lg prose-img:my-8"
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />
            ) : (
              <div className="text-center py-12 text-gray-400">
                <p>Content for this blog post is being prepared.</p>
                <p>Please check back later.</p>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default BlogPost;
